<script lang="ts">
import {defineComponent, shallowRef, type ComponentPublicInstance} from 'vue';
import type {ViewBoxController} from '../allspice_render/viewbox_controller.ts';
import {setupViewBoxPanZoom, dollyZoomToSelector, type ViewBoxPanZoomHandle} from '../allspice_render/viewbox_panzoom.ts';
import {afterSnippetClickThrough, fitViewCoordinates} from '../allspice_render/view_coordinates_zoom.ts';

import GeneratedInlineSvg from '../GeneratedInlineSvg.vue';
import {FetchRetryer} from '../../fetchretryer.ts';
import {SVG} from '../svg_utils.ts';
import {svgSimpleFilter} from './AllSpiceDiffViewStyle.ts';
import {buildPreviewRoute, buildDiffPreviewRoute} from './AllSpiceDiffViewRouteBuilder.ts';
import {PreviewLoader} from '../../features/loadpreview.ts';
import {selectRegionParent} from '../../features/selectregion.ts';
import {onNextFrame, waitForRender} from '../../domutils.ts';
import {AllSpiceJsonLoader, SCHEMA_TYPES} from '../../features/allspicejson.ts';
import EcadInspectorFrame from '../EcadInspectorFrame.vue';
import {toggleHighlightElement} from '../document_highlight.ts';
import {applyColorProfile} from '../pcb_colors.ts';
import {bakePourMasks} from '../allspice_render/bake_pour_masks.ts';
import {useDiffToggleStore} from '../../allspice_app/stores/diffToggle.ts';
import {useZoomLockStore} from '../../allspice_app/stores/zoomLock.ts';
import {refreshDiffLayers} from './diff-layer-utils.ts';
import {ATTRIBUTES_DELIMITER, determineSchemaType, normalizeFilterPath, overlayDiffVisibilityKey} from './diff-filter-shared.ts';
import {buildFilterDataFromContents} from './diff-filter-data.ts';

import AllSpiceSnippetReference from '../AllSpiceSnippetReference.vue';
import {resolveInspectorEntity} from '../inspector_entity_lookup.ts';
import type {InspectorDiffTreeState} from '../inspector_diff_tree.ts';
import {InspectorContext, headOrBase, type TreeNode} from '../inspector_tree.ts';
import {collectDiffOverlaySuppressedTargets} from '../../allspice_app/components/diff-list/diff-grouping.ts';
import {findPageContainer} from '../../allspice_app/components/diff-list/page-navigation.ts';
import {hideElem, showElem} from '../../utils/dom.ts';
import {isAltiumSchematic} from '../../utils/schematic-format.ts';
import {AllSpiceComponentRefs} from '../../features/allspicecomprefs.ts';
import {event} from '../../analytics.ts';
import type {ProjectData} from '../../allspice_app/types/project-data.ts';
import type {Container} from '../../container.ts';
import type {InspectorDataLoadEventDetails} from '../allspice_render/types.ts';
import type {Layer} from '../../allspice_app/stores/layers.ts';

const {assetUrlPrefix} = window.config;

export type AllSpiceDiffViewData = {
  id: string,
  isLoading: boolean,
  isLoadError: boolean,
  isHeadLoaded: boolean,
  isDiffFromLoaded: boolean,
  isDiffToLoaded: boolean,
  isDiffLoadError: boolean,
  showErrorDetails: boolean,
  errorDetailsText: string,
  panZoomHelpText: string,
  panZoomHelpClassNames: string,
  clientX: number,
  clientY: number,
  panZoom: ViewBoxController | undefined,
  panZoomHandle: ViewBoxPanZoomHandle | null,
  currentNet: string,
  isLoadingIndicatorEnabled: boolean,
  referenceElement: SVGSVGElement | null,
  foldedModel: boolean,
  allLoaded: boolean,
  foldObserver: MutationObserver | null,
  isFlipped: boolean,
  baseContents: Record<string, unknown> | null,
  headContents: Record<string, unknown> | null,
  prjJson: ProjectData | null,
  isPcb: boolean,
  isDiffLoading: boolean,
  nets: Record<string, unknown>,
};

export default defineComponent({
  name: 'AllSpiceDiffView',
  components: {
    'generated-inline-svg': GeneratedInlineSvg,
    EcadInspectorFrame,
    'allspice-snippet-reference': AllSpiceSnippetReference,
  },
  inject: {
    untypedContainer: {from: 'container'},
    inspectorDiffTreeState: {from: 'inspectorDiffTreeState', default: undefined},
  },
  props: {
    // True for a diff between two commits; false for viewing a single file (uses head params).
    isDiff: {
      type: Boolean,
      required: false,
      default: true,
    },
    baseRepoOwner: {
      type: String,
      required: false,
      default: '',
    },
    baseRepoName: {
      type: String,
      required: false,
      default: '',
    },
    baseCommit: {
      type: String,
      required: false,
      default: '',
    },
    baseTreePath: {
      type: String,
      required: false,
      default: '',
    },
    headRepoOwner: {
      type: String,
      required: true,
      default: '',
    },
    headRepoName: {
      type: String,
      required: true,
      default: '',
    },
    headCommit: {
      type: String,
      required: true,
      default: '',
    },
    headTreePath: {
      type: String,
      required: true,
      default: '',
    },
    // Value is either three dots or two dots.
    diffType: {
      type: String,
      required: false,
      default: '...',
    },
    // Whether panning and zooming is enabled.
    isPanZoomEnabled: {
      type: Boolean,
      required: false,
      default: true,
    },
    // Alt text for accessibility.
    alt: {
      type: String,
      required: false,
      default: '',
    },
    // The SVG class(es): as-snippet, as-thumbnail.
    svgClassNames: {
      type: String,
      required: false,
      default: '',
    },
    dataAsViewCoords: {
      type: String,
      required: false,
      default: '',
    },
    // Width-to-height aspect ratio.
    dataAsAspectRatio: {
      type: String,
      required: false,
      default: '',
    },
    // When true and loading the image errors, displays a default image.
    displayDefaultOnError: {
      type: Boolean,
      required: false,
      default: true,
    },
    colorProfile: {
      type: Object,
      required: false,
      default: null,
    },
    folded: {
      type: Boolean,
      required: false,
      default: false,
    },
  },

  setup() {
    const headLoadPreview = shallowRef<PreviewLoader | null>(null);
    const diffLoadPreview = shallowRef<PreviewLoader | null>(null);
    const getDiffFrom = shallowRef<FetchRetryer | null>(null);
    const getDiffTo = shallowRef<FetchRetryer | null>(null);

    return {
      headLoadPreview,
      diffLoadPreview,
      getDiffFrom,
      getDiffTo,
    };
  },

  data(): AllSpiceDiffViewData {
    return {
      id: '',
      isLoading: true,
      isLoadError: false,
      isHeadLoaded: false,
      isDiffFromLoaded: false,
      isDiffToLoaded: false,
      isDiffLoadError: false,
      showErrorDetails: false,
      errorDetailsText: '',
      panZoomHelpText: '',
      panZoomHelpClassNames: '',
      clientX: NaN,
      clientY: NaN,
      panZoom: undefined,
      panZoomHandle: null,
      currentNet: '',
      isLoadingIndicatorEnabled: false,
      referenceElement: null,
      foldedModel: this.folded,
      allLoaded: false,
      foldObserver: null,
      isFlipped: false,
      baseContents: null,
      headContents: null,
      prjJson: null,
      isPcb: false,
      isDiffLoading: false,
      nets: {} as Record<string, unknown>,
    };
  },

  computed: {
    container(): Container {
      return this.untypedContainer as unknown as Container;
    },
    headPreviewUrl() {
      return buildPreviewRoute(
        this.container,
        this.headRepoOwner,
        this.headRepoName,
        this.headCommit,
        this.headTreePath);
    },
    diffFromPreviewUrl() {
      if (!this.baseTreePath) return '';
      return buildDiffPreviewRoute(
        this.container,
        this.headRepoOwner,
        this.headRepoName,
        'from',
        this.headCommit,
        this.headTreePath,
        this.baseRepoOwner,
        this.baseRepoName,
        this.baseCommit,
        this.baseTreePath);
    },
    diffToPreviewUrl() {
      if (!this.headTreePath) return '';
      return buildDiffPreviewRoute(
        this.container,
        this.headRepoOwner,
        this.headRepoName,
        'to',
        this.headCommit,
        this.headTreePath,
        this.baseRepoOwner,
        this.baseRepoName,
        this.baseCommit,
        this.baseTreePath);
    },
    svgContainerClassNames() {
      // This should match setupSnippetContainer().
      const svgClasses = (this.svgClassNames || '').split(' ');
      const arr = [];
      if (svgClasses.includes('as-snippet')) {
        arr.push('as-snippet-container');
      }
      if (svgClasses.includes('as-thumbnail')) {
        arr.push('as-thumbnail-container');
      }
      return arr;
    },
    failedImageUrl() {
      return `${assetUrlPrefix}/img/failed.svg`;
    },
  },

  created() {
    const headTreePathLower = this.headTreePath.toLowerCase();
    this.isPcb = headTreePathLower.endsWith('.pcbdoc') || headTreePathLower.endsWith('.cspcbdoc') || headTreePathLower.endsWith('.kicad_pcb') || headTreePathLower.endsWith('.brd');

    if (!this.isDiff) return;

    this.getDiffFrom = FetchRetryer.for(this.container, this.diffFromPreviewUrl, {fetchImmediately: true, skipCache: true});
    this.getDiffTo = FetchRetryer.for(this.container, this.diffToPreviewUrl, {fetchImmediately: true, skipCache: true});

    this.diffLoadPreview = new PreviewLoader({
      type: 'diff',
      repoOwnerName: this.headRepoOwner,
      repoName: this.headRepoName,
      headTreePath: this.headTreePath,
      headCommitID: this.headCommit,
      baseCommitID: this.baseCommit,
    }, this.diffLoadCallback, this.diffLoadErrorCallback);
  },

  mounted() {
    // Snippet click-thru is used only within design reviews.
    document.addEventListener('allspice-snippet-click-thru', this.setupSnippetReference);
    this.id = Math.random().toString(16).slice(2);

    if (this.isDiff) {
      this.foldObserver = new MutationObserver((mutationList) => {
        for (const mutation of mutationList) {
          if (mutation.attributeName === 'data-folded' && (mutation.target as Element).getAttribute('data-folded') === 'false') {
            this.onUnfold();
          }

          if (mutation.attributeName === 'data-mounted' && (mutation.target as Element).getAttribute('data-mounted') === 'true') {
            this.setupPanZoom();
          }
        }
      });
      this.foldObserver.observe(this.$el.closest('.diff-file-box'), {attributes: true, attributeFilter: ['data-folded', 'data-mounted']});
    }

    // Must wait until mounted: the callback relies on this.$refs.
    this.headLoadPreview = new PreviewLoader({
      type: 'convert',
      repoOwnerName: this.headRepoOwner,
      repoName: this.headRepoName,
      treePath: this.headTreePath,
      commitID: this.headCommit,
    }, this.loadCallback, this.loadErrorCallback);
  },

  unmounted() {
    this.removeGlobalEventHandlers();
    document.removeEventListener('allspice-snippet-click-thru', this.setupSnippetReference);
    document.removeEventListener('allspice-pcb-flip', this.onFlip);
    if (this.foldObserver) {
      this.foldObserver.disconnect();
    }
    this.panZoomHandle?.destroy();
  },

  methods: {
    // Called when we're ready to attempt to load the head files
    loadCallback() {},
    // There was an error loading the head file
    loadErrorCallback() {
      this.displayLoadError();
      this.headLoadPreview.cancel();
      this.isLoading = false;
      this.isLoadError = true;
      this.diffLoadPreview?.cancel();
    },
    // Called when we're ready to attempt to load the diff files
    diffLoadCallback() {
      if (this.isLoadError || !this.isDiffLoading) return;

      this.getDiffFrom.hintReadyToFetch();
      this.getDiffTo.hintReadyToFetch();
    },
    diffLoadErrorCallback() {
      this.diffLoadPreview.cancel();
      this.isDiffLoadError = true;
      this.displayDiffResponseError();
    },
    onHeadSvgError(fetchRetryer: FetchRetryer | null) {
      if (fetchRetryer?.response) {
        console.error(fetchRetryer.response);
      }
      this.displayLoadError();
      this.headLoadPreview.cancel();
      this.isLoading = false;
      this.isLoadError = true;
      this.diffLoadPreview?.cancel();
    },
    displayLoadError() {
      console.error(`Head file could not load.`);
      this.errorDetailsText = `Error: Failed to load file from server. Please contact maintainers@allspice.io if this issue persists.`;
      this.showErrorDetails = true;
      this.isLoading = false;
      this.isLoadError = true;
    },

    /**
     * @param {Response | undefined} resp
     */
    displayDiffResponseError(resp?: Response) {
      console.error(`Diff resp not found. Status code: ${resp?.status}`);
      if (resp) {
        this.errorDetailsText = `Error: Failed to receive diff from server. Status code: ${resp.status}. Please contact maintainers@allspice.io if this issue persists.`;
      } else {
        this.errorDetailsText = `Error: Failed to receive diff from server. Please contact maintainers@allspice.io if this issue persists.`;
      }
      this.showErrorDetails = true;
    },

    displayUnknownError(err: unknown) {
      console.error(err);
      this.errorDetailsText = 'Error: Failed to load diff. Please contact maintainers@allspice.io if this issue persists.';
      this.showErrorDetails = true;
    },

    transformContainer() {
      this.headLoadPreview.cancel();

      if (!this.foldedModel) {
        // SVG viewBox is defined now; setting up panzoom after children avoids a large layout pass.
        this.setupPanZoom();
      }
    },

    async transformChildren(blob: Blob) {
      if (this.isDiff) {
        const [d, t] = await Promise.all([this.insertDiffFrom(), this.insertDiffTo()]);

        this.diffLoadPreview?.cancel();

        return new Blob(['<g class="working">', blob, '</g>', d, t]);
      }
      return blob;
    },

    /**
     * Called when the head file has been fetched, but before it's inlined into
     * the DOM.
     * @param {SVGSVGElement | null} sharedSvg
     * @returns {SVGSVGElement} This needs to return either the input element or
     * a new element of the same type.
     */
    async transformHeadSvg(svg: SVGSVGElement | null) {
      if (svg === null) {
        console.error("Couldn't find working SVG top-level element");
        return svg;
      }

      // If we're diffing, we mutate the SVG.
      if (this.isDiff) {
        svgSimpleFilter(svg, this.id);
      }

      if (this.colorProfile !== null) {
        applyColorProfile(svg, this.colorProfile);
      }

      return svg;
    },

    async diffSVG(url: string | null, promise: Promise<FetchRetryer>, className: string) {
      if (!url) {
        return `<g class="diff ${className}"></g>`;
      }

      try {
        const fetcher = await promise;
        const blob = await fetcher.response?.blob();
        const partial = await blob.slice(0, 512 * 1024).text();
        const svg = new SVG(partial, blob);
        return new Blob([
          `<g id="${svg.id}" class="diff ${className}">`,
          svg.children,
          '</g>',
        ]);
      } catch (e) {
        console.error(e);
        this.onDiffError(e);
      }

      return `<g class="diff ${className}"></g>`;
    },

    async insertDiffFrom() {
      return this.diffSVG(this.diffFromPreviewUrl, this.getDiffFrom.promise, 'diff_from');
    },

    async insertDiffTo() {
      return this.diffSVG(this.diffToPreviewUrl, this.getDiffTo.promise, 'diff_to');
    },

    async onHeadSvgLoad() {
      // Guard against the load event firing more than once.
      if (this.isHeadLoaded) {
        return;
      }
      const $svg: SVGSVGElement = (this.$refs.inlineSvg as ComponentPublicInstance).$el;
      if ($svg === null) {
        console.error("Couldn't find working SVG top-level element");
        return;
      }

      this.isHeadLoaded = true;

      if (this.isPcb) {
        bakePourMasks($svg);
      }

      // Notify that the preview loaded — only after the SVG is in the DOM.
      const loadedEvent = new CustomEvent('previewload', {
        bubbles: true,
        detail: {
          headTreePath: this.headTreePath,
        },
      });
      $svg.dispatchEvent(loadedEvent);

      if (!this.foldedModel) {
        await this.completeSetup();
      }

      this.isLoading = false;
      this.allLoaded = true;
    },

    /**
     * Complete setup once all svgs loaded, concatenated
     */
    async completeSetup() {
      this.setupPanZoom();

      // Let the ResizeObserver (from setupPanZoom) center the container first; snippet
      // reference needs accurate element dimensions.
      await waitForRender();
      await this.handleComponentLink();
      this.setupSnippetReference();
      this.enableDiffToggle();

      // listen for the flip toggle
      document.addEventListener('allspice-pcb-flip', this.onFlip);

      // load project and design json
      await this.loadAllSpicePrjJson();
    },

    /**
     * Called when a diff view is unfolded for the first time
     */
    onUnfold() {
      if (this.allLoaded && this.foldedModel) {
        // Already loaded while folded; finish the deferred setup now.
        this.completeSetup();
      }
      this.foldedModel = false;
    },

    onFlip(ev: Event) {
      const customEvent = ev as CustomEvent<{headTreePath: string; isFlipped: boolean}>;
      const headTreePath = customEvent.detail.headTreePath;
      // only flip if it's *our* flip toggle
      if (headTreePath === this.headTreePath) {
        this.flip(customEvent.detail.isFlipped);
      }
    },

    /**
     * Once layers are loaded, enable the diff toggle buttons
     */
    enableDiffToggle() {
      if (this.isDiff) {
        useDiffToggleStore().enable();
        // Apply the current (possibly persisted, e.g. 'added') toggle state to the fresh SVG.
        refreshDiffLayers(this.$el.closest('.diff-file-box') ?? document);
      }
    },

    /**
     * Load AllSpice json documents once svgs are loaded
     * @param {object} prjJson
     */
    loadAllSpiceDesignJson(prjJson: ProjectData) {
      // load json for inspector
      AllSpiceJsonLoader.load(this.container,
        AllSpiceJsonLoader.buildUrl(this.container,
          this.headRepoOwner, this.headRepoName, this.headCommit, this.headTreePath),
        AllSpiceJsonLoader.buildUrl(this.container,
          this.baseRepoOwner, this.baseRepoName, this.baseCommit, this.baseTreePath))
        // eslint-disable-next-line github/no-then
        .then((results) => {
          this.onAllSpiceJsonLoad(results.headJson, results.baseJson, prjJson);
        })
        // eslint-disable-next-line github/no-then
        .catch(console.error.bind(console));
    },

    /**
     * Load project json to update svg with component refs
     */
    async loadAllSpicePrjJson() {
      try {
        // Load project data for all file types so variants are available.
        const prjUrl = AllSpiceJsonLoader.buildPrjUrl(this.container,
          this.headRepoOwner, this.headRepoName, this.headCommit, this.headTreePath);
        const results = await AllSpiceJsonLoader.load<ProjectData>(this.container, prjUrl, null);
        const $svg: SVGSVGElement = (this.$refs.inlineSvg as ComponentPublicInstance).$el;

        // load the design json with project data
        this.loadAllSpiceDesignJson(results.headJson);

        // Altium schematics may have component references that need to be resolved
        if (isAltiumSchematic(this.headTreePath)) {
          const headUrls = AllSpiceComponentRefs.buildSvgUrls(
            this.container,
            this.headRepoOwner,
            this.headRepoName,
            this.headCommit,
            results.headJson?.documents ?? {},
          );

          return AllSpiceComponentRefs.forSvg(this.container, $svg, headUrls);
        }
      } catch (error) {
        console.error('Failed to load project data:', error);
        // Fallback to loading design json without project data
        this.loadAllSpiceDesignJson(null);
      }
    },

    /**
     * Determine whether select region mode is enabled
     * @return {boolean}
     */
    selectRegionEnabled() {
      const parent = this.$el.closest('.diff-file-box.has-selectable-region') ||
        this.$el.closest('.non-diff-file-content.has-selectable-region');

      if (parent) {
        return parent.classList.contains('select-region-mode');
      }
      return false;
    },

    /**
     * Toggle the current net on/off
     * @param {SVGSVGElement} svg the svg to toggle
     * @param {boolean} toggleOn whether the net is being toggled on or off
     * @param {boolean} closeInspector whether to close inspector when done
     * @returns {Promise<void>}
     */
    toggleNet(svg: SVGSVGElement | null, toggleOn: boolean, closeInspector: boolean) {
      return new Promise<void>((resolve, reject) => {
        onNextFrame(() => {
          try {
            if (toggleOn) {
              toggleHighlightElement(svg, `[data-net="${CSS.escape(this.currentNet)}"]`, true, 'net', this.isPcb);
              // refresh the diff toggle state
              refreshDiffLayers(this.$el.closest('.diff-file-box') ?? document);
            } else if (this.currentNet !== '') {
              // unhighlight the last net
              toggleHighlightElement(svg, `[data-net="${CSS.escape(this.currentNet)}"]`, false, 'net', this.isPcb);
              this.currentNet = '';
            }
            if (closeInspector) {
              (this.$refs.ecadInspectorFrame as typeof EcadInspectorFrame).showInspector(false);
            }
            resolve(null);
          } catch (error) {
            reject(error);
          }
        });
      });
    },

    /**
     * Toggle the loading indicator and set flag
     * @param {boolean} isLoading
     */
    setLoadingIndicator(isLoading: boolean) {
      this.isLoadingIndicatorEnabled = isLoading;
      const loadingIndicator = this.findASLoading();
      if (loadingIndicator !== null) {
        if (isLoading) {
          showElem(loadingIndicator);
        } else {
          hideElem(loadingIndicator);
        }
      }
    },

    /**
     * Event handler setup
     * @param {object} headContents json contents for head
     * @param {object} baseContents json contents for base
     * @param {object} prjJson project level json if applicable
     */
    onAllSpiceJsonLoad(headContents: Record<string, any>, baseContents: Record<string, any>, prjJson: ProjectData) {
      // Keep the full multi-page JSON before extracting page 0; DocumentSearch indexes all pages.
      const fullHeadContents = headContents;
      const fullBaseContents = baseContents;

      // For a crysknife schematic, render only the first page.
      if (headContents && headContents.pages) {
        headContents = headContents.pages[0] || headContents;
      }
      if (baseContents && baseContents.pages) {
        baseContents = baseContents.pages[0] || baseContents;
      }

      this.$data.baseContents = baseContents;
      this.$data.headContents = headContents;
      this.$data.prjJson = prjJson;

      this.hideNonVisualDiffOverlay(fullHeadContents, fullBaseContents);

      // Combine nets from the full JSON (crysknife stores nets at root, not per-page).
      const combinedNets = fullHeadContents?.nets ?? headContents?.nets ?? {};
      // Add any base-only (deleted) nets.
      for (const [netId, netInfo] of Object.entries(fullBaseContents?.nets ?? baseContents?.nets ?? {})) {
        if (!combinedNets[netId]) {
          combinedNets[netId] = netInfo;
        }
      }
      this.$data.nets = combinedNets;

      /** @type {SVGSVGElement} */
      const $svg: SVGSVGElement = (this.$refs.inlineSvg as ComponentPublicInstance).$el;

      // emit json load event
      $svg.dispatchEvent(new CustomEvent<InspectorDataLoadEventDetails>('inspectordataload', {
        bubbles: true,
        detail: {
          baseContents,
          headContents,
          layers: headOrBase(headContents, baseContents, 'layers', {}) as Record<number, Layer>,
          presets: headOrBase(headContents, baseContents, 'preset_layers', {}) as Record<string, number[]>,
          // PCB components are stored separately
          pcbComponents: headOrBase(headContents, baseContents, 'component_instances', {}),
          // SCH components stored inline
          schComponents: (headContents !== null) ? headContents : ((baseContents !== null) ? baseContents : {}),
          // Full multi-page JSON for cross-page search; set only when the JSON had .pages[].
          allPagesHead: fullHeadContents?.pages ? fullHeadContents : null,
          allPagesBase: fullBaseContents?.pages ? fullBaseContents : null,
          nets: this.$data.nets ?? {},
        },
      }));

      // Remove event handlers that were setup during a previous call.
      this.removeGlobalEventHandlers();
      document.body.addEventListener('keydown', this.onKeyDown);
      document.body.addEventListener('inspector-hide', this.handleinspectorHide);
      document.body.addEventListener('dolly-zoom-request', this.handleDollyZoomRequest);

      // prevent double-registering event handlers
      $svg.removeEventListener('inspector-click', this.handleInspectorClick);
      $svg.addEventListener('inspector-click', this.handleInspectorClick);

      // prevent double-registering event handlers
      $svg.removeEventListener('inspector-request', this.handleInspectorRequest);
      $svg.addEventListener('inspector-request', this.handleInspectorRequest);
    },

    hideNonVisualDiffOverlay(headContents: Record<string, any> | null, baseContents: Record<string, any> | null) {
      if (!this.isDiff) {
        return;
      }

      const inspectorDiffTreeState = this.inspectorDiffTreeState as InspectorDiffTreeState | undefined;
      const diffTree = inspectorDiffTreeState?.getActiveInspectorDiffTree();
      const schemaType = determineSchemaType(headContents, baseContents);
      const suppressedTargets = diffTree ? collectDiffOverlaySuppressedTargets(schemaType, diffTree) : [];
      const supportsNonVisualDiffOverlaySuppression =
        schemaType === SCHEMA_TYPES.SCHEMATIC ||
        schemaType === SCHEMA_TYPES.SINGLE_PAGE_SCHEMATIC;

      const root = (this.$el.closest('.diff-file-box') ?? this.$el) as Element;

      for (const hiddenElement of root.querySelectorAll<SVGElement>('[data-hidden-nonvisual-diff="true"]')) {
        hiddenElement.style.removeProperty('visibility');
        hiddenElement.removeAttribute('data-hidden-nonvisual-diff');
      }

      if (!supportsNonVisualDiffOverlaySuppression) {
        return;
      }

      if (diffTree) {
        // The diff tree is authoritative: hide exactly the elements whose
        // only changes are non-visual. An empty list means nothing to hide.
        for (const {pageId, selector} of suppressedTargets) {
          const scopedRoot = pageId ? (findPageContainer(root, pageId) ?? root) : root;
          const diffSvgs = scopedRoot.querySelectorAll('.diff_from, .diff_to');

          for (const diffSvg of diffSvgs) {
            const elements = diffSvg.querySelectorAll<SVGElement>(selector);
            for (const element of elements) {
              element.style.visibility = 'hidden';
              element.setAttribute('data-hidden-nonvisual-diff', 'true');
            }
          }
        }
        return;
      }

      // No diff tree available: fall back to comparing the JSON contents.
      // Only hide elements the filter data positively indexes as having no
      // meaningful changes; leave unindexed elements visible.
      for (const {pageId, visibleTargets} of this.collectMeaningfulDiffTargets(headContents, baseContents)) {
        const scopedRoot = pageId ? (findPageContainer(root, pageId) ?? root) : root;
        for (const element of scopedRoot.querySelectorAll<SVGElement>('.diff_from [data-id][data-path], .diff_to [data-id][data-path]')) {
          const dataId = element.getAttribute('data-id');
          const dataPath = element.getAttribute('data-path');
          if (!dataId || !dataPath) {
            continue;
          }

          const visibilityKey = overlayDiffVisibilityKey(dataPath, dataId);
          if (visibilityKey !== null && !visibleTargets.has(visibilityKey)) {
            element.style.visibility = 'hidden';
            element.setAttribute('data-hidden-nonvisual-diff', 'true');
          }
        }
      }
    },

    collectMeaningfulDiffTargets(headContents: Record<string, any> | null, baseContents: Record<string, any> | null) {
      const headPages = headContents?.pages;
      const basePages = baseContents?.pages;

      if (headPages || basePages) {
        const pageCount = Math.max(headPages?.length ?? 0, basePages?.length ?? 0);
        const pageTargets: Array<{pageId: string | null; visibleTargets: Set<string>}> = [];

        for (let index = 0; index < pageCount; index++) {
          const headPage = headPages?.[index] ?? null;
          const basePage = basePages?.[index] ?? null;
          pageTargets.push({
            pageId: headOrBase(headPage, basePage, 'id', null) as string | null,
            visibleTargets: this.buildMeaningfulDiffTargetSet(headPage, basePage),
          });
        }

        return pageTargets;
      }

      return [{
        pageId: null,
        visibleTargets: this.buildMeaningfulDiffTargetSet(headContents, baseContents),
      }];
    },

    buildMeaningfulDiffTargetSet(headContents: Record<string, any> | null, baseContents: Record<string, any> | null) {
      try {
        const {dataIdsToAttributes} = buildFilterDataFromContents(headContents, baseContents, {});
        const visibleTargets = new Set<string>();

        for (const [dataId, attributePaths] of Object.entries(dataIdsToAttributes)) {
          for (const targetPath of this.extractMeaningfulTargetPaths(attributePaths)) {
            visibleTargets.add(`${targetPath}${ATTRIBUTES_DELIMITER}${dataId}`);
          }
        }

        return visibleTargets;
      } catch (error) {
        console.error(error);
        return new Set<string>();
      }
    },

    extractMeaningfulTargetPaths(attributePaths: string[]) {
      const targetPaths = new Set<string>();

      for (const attributePath of attributePaths) {
        const normalizedPath = normalizeFilterPath(attributePath);
        if (!normalizedPath) {
          continue;
        }

        const pathSegments = normalizedPath.split(ATTRIBUTES_DELIMITER).filter(Boolean);
        if (pathSegments.length === 0) {
          continue;
        }

        if (pathSegments[0] === 'graphics' && pathSegments[1]) {
          targetPaths.add(pathSegments[1]);
          continue;
        }

        targetPaths.add(pathSegments[0]);
      }

      return targetPaths;
    },

    async handleInspectorClick(e: Event) {
      e.preventDefault();

      const customEvent = e as CustomEvent<{target: Element; clientX: number; clientY: number}>;
      const $svg: SVGSVGElement = (this.$refs.inlineSvg as ComponentPublicInstance).$el;

      const inspectorDiffTreeState = this.inspectorDiffTreeState as InspectorDiffTreeState | undefined;
      const prebuiltTree = inspectorDiffTreeState?.findPrebuiltInspectorTreeForElement(customEvent.detail.target) ?? null;

      this.showInspectorForElement(customEvent.detail.target, $svg, this.$data.baseContents, this.$data.headContents, customEvent, prebuiltTree);
    },

    async handleInspectorRequest(e: Event) {
      const {target, selector, treeNode} = (e as CustomEvent<{target: Element; selector: string; treeNode?: TreeNode | null}>).detail;
      const $svg: SVGSVGElement = (this.$refs.inlineSvg as ComponentPublicInstance).$el;

      // pan and zoom to the element before popping up the inspector
      await this.dollyZoom($svg, selector);

      const inspectorDiffTreeState = this.inspectorDiffTreeState as InspectorDiffTreeState | undefined;
      const prebuiltTree = treeNode ?? inspectorDiffTreeState?.findPrebuiltInspectorTreeForElement(target) ?? null;

      this.showInspectorForElement(target, $svg, this.$data.baseContents, this.$data.headContents, null, prebuiltTree);
    },

    async handleinspectorHide() {
      this.hideInspector();
    },

    buildInspectorMetadataContext() {
      const layers = headOrBase(this.$data.headContents, this.$data.baseContents, 'layers', {}) as Record<number, {id: number, name: string}>;
      const externalDesignBlocks = this.$data.prjJson ? this.$data.prjJson['external_design_blocks'] : null;
      const projectDocuments = this.$data.prjJson ? this.$data.prjJson['documents'] : null;
      const projectPath = this.$data.prjJson ? this.$data.prjJson['project_path'] : null;

      return new InspectorContext({
        window,
        treePath: this.headTreePath,
        repoOwner: this.headRepoOwner,
        repoName: this.headRepoName,
        commitSHA: this.headCommit,
        layers,
        externalDesignBlocks,
        projectDocuments,
        projectPath,
        variants: this.$data.prjJson?.variants || {},
        options: {
          replaceLayerIdsWithNames: true,
        },
      });
    },

    showInspectorForElement(targetEl: Element, svgEl: SVGSVGElement, baseContents: Record<string, any>, headContents: Record<string, any>, inspectorClickEvent: CustomEvent | null = null, prebuiltTree: TreeNode | null = null) {
        // Ignore the click while a net is loading.
      if (this.isLoadingIndicatorEnabled) {
        return;
      }

      targetEl = targetEl.closest('[data-id]') ?? targetEl;
      const willShowNet = this.currentNet !== '' || targetEl.hasAttribute('data-net');

      let elementId = targetEl.getAttribute('data-id');
      if (elementId && !this.selectRegionEnabled()) {
        (this.$refs.ecadInspectorFrame as typeof EcadInspectorFrame).showInspector(false);

        this.setLoadingIndicator(true);

        let elemHeadContents = headContents;
        let elemBaseContents = baseContents;
        let type = '';

        if (targetEl.hasAttribute('data-path')) {
          let dataPath = targetEl.getAttribute('data-path').split('/');

            // A primitive belonging to a component: show the whole component, not the primitive.
          if (dataPath.length > 1 && dataPath[0] === 'component_instances') {
            elementId = dataPath[1];
            dataPath = ['component_instances'];
            type = 'Component';
          } else {
            // ex: 'texts' -> 'Text'
            const group = dataPath[0].endsWith('s') ? dataPath[0].slice(0, -1) : dataPath[0];
            type = group.charAt(0).toUpperCase() + group.slice(1);
          }

          for (const segment of dataPath) {
            elemHeadContents = headOrBase(elemHeadContents, {}, segment, null);
            elemBaseContents = headOrBase(elemBaseContents, {}, segment, null);
          }

          const resolvedEntity = resolveInspectorEntity(
            elemHeadContents,
            elemBaseContents,
            elementId,
            dataPath,
            headOrBase,
            (this.inspectorDiffTreeState as InspectorDiffTreeState | undefined)?.getRawDiffJson(),
          );
          elemHeadContents = resolvedEntity.headEntity;
          elemBaseContents = resolvedEntity.baseEntity;
        } else {
          const resolvedEntity = resolveInspectorEntity(
            elemHeadContents,
            elemBaseContents,
            elementId,
            [],
            headOrBase,
            (this.inspectorDiffTreeState as InspectorDiffTreeState | undefined)?.getRawDiffJson(),
          );
          elemHeadContents = resolvedEntity.headEntity;
          elemBaseContents = resolvedEntity.baseEntity;
        }

          // Use the click's mouse position if there was one; otherwise center on the element.
        const {x, y, width, height} = targetEl.getBoundingClientRect();
        const location = inspectorClickEvent === null ? {
          clientX: x + (width / 2),
          clientY: y + (height / 2),
        } : inspectorClickEvent.detail;

        const metadataCtx = this.buildInspectorMetadataContext();

        (this.$refs.ecadInspectorFrame as typeof EcadInspectorFrame).setInspectorData(location,
          this.$refs.wrapper,
          elemHeadContents,
          elemBaseContents,
          headOrBase(elemHeadContents, elemBaseContents, 'type', type),
          targetEl.getAttribute('data-variant'),
          elementId,
          this.isPcb ? headOrBase(headContents, baseContents, 'units', 'mm') : '',
          metadataCtx,
          prebuiltTree,
        ).finally(() => {
          if (!willShowNet) {
            this.setLoadingIndicator(false);
          }
        });
      } else {
        (this.$refs.ecadInspectorFrame as typeof EcadInspectorFrame).showInspector(false);
      }

      if (willShowNet) {
        this.setLoadingIndicator(true);

          // Toggle off any previous net first.
          // eslint-disable-next-line github/no-then
        this.toggleNet(svgEl, false, false).then(() => {
          const net = targetEl.getAttribute('data-net');
          if (net && !this.selectRegionEnabled()) {
            this.currentNet = net;
            return this.toggleNet(svgEl, true, false);
          }
        }).finally(() => {
          this.setLoadingIndicator(false);
        });
      }
    },

    hideInspector() {
      (this.$refs.ecadInspectorFrame as typeof EcadInspectorFrame).showInspector(false);
    },

    async handleDollyZoomRequest(e: Event) {
      const {selector} = (e as CustomEvent<{selector: string}>).detail;
      const $svg: SVGSVGElement = (this.$refs.inlineSvg as ComponentPublicInstance).$el;

      // The event is global, so bail if the selector doesn't match this instance's SVG.
      if (!$svg?.querySelector(selector)) return;

      await this.dollyZoom($svg, selector);
    },

    removeGlobalEventHandlers() {
      document.body.removeEventListener('keydown', this.onKeyDown);
      document.body.removeEventListener('inspector-hide', this.handleinspectorHide);
      document.body.removeEventListener('dolly-zoom-request', this.handleDollyZoomRequest);
    },

    /**
     * @param {KeyboardEvent} e
     */
    onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        (this.$refs.ecadInspectorFrame as typeof EcadInspectorFrame).showInspector(false);
      }
    },

    /**
     * Close inspector window and unhighlight net
     */
    inspectorNetClose() {
      // Ignore while a net is loading.
      if (!this.isLoadingIndicatorEnabled && this.currentNet !== '') {
        this.setLoadingIndicator(true);

        const $svg: SVGSVGElement = (this.$refs.inlineSvg as ComponentPublicInstance).$el;
        // Toggle off the selected net and close the popup.
        // eslint-disable-next-line github/no-then
        this.toggleNet($svg, false, true).then(() => {
          this.setLoadingIndicator(false);
        });
      }
    },

    setupPanZoom() {
      if (!this.isPanZoomEnabled || this.panZoom) return;

      const elem = this.$refs.panZoom as HTMLElement;
      const region = this.$refs.selectableRegion as HTMLElement;
      const svg = (this.$refs.inlineSvg as ComponentPublicInstance | undefined)?.$el as SVGSVGElement | null;
      // The SVG may not be in the DOM yet; we'll be called again once it renders.
      if (!region || !svg) return;

      const handle = setupViewBoxPanZoom({
        elem,
        region,
        svg,
        inspectorTarget: svg,
        onScrollZoom: () => {
          this.panZoomHelpClassNames = 'hidden';
        },
        onUserZoom: (scale) => {
          useZoomLockStore().recordScale(scale);
        },
      });
      this.panZoom = handle.controller;
      this.panZoomHandle = handle;
      this.panZoomHelpText = `${handle.keyModifier} + Scroll to zoom`;
    },

    /** Highlight and optionally frame the URL region only in the selected diff file. */
    setupSnippetReference(event?: Event) {
      this.referenceElement = null;
      const queryString = window.location.search;
      const queryParameters = new URLSearchParams(queryString);

      if (queryParameters.has('view-coordinates')) {
        const anchor = queryParameters.get('anchor');
        const referenceElement = this.$el.querySelector('svg.schematic_svg, svg.schematic') as SVGSVGElement | null;
        // Only the selected diff file should respond to the URL coordinates.
        if (!referenceElement || (anchor && !referenceElement.closest(`#${CSS.escape(anchor)}`))) return;
        this.referenceElement = referenceElement;
        afterSnippetClickThrough(event, () => {
          fitViewCoordinates(queryParameters, referenceElement, this.panZoom);
        });
      }
    },

    /**
     * If a component is directly referenced in the url, pan and zoom to center that component
     */
    async handleComponentLink() {
      const queryParameters = new URLSearchParams(window.location.search);

      if (queryParameters.has('comp')) {
        const $svg: SVGSVGElement = (this.$refs.inlineSvg as ComponentPublicInstance).$el;
        const selector = `[data-id="${CSS.escape(queryParameters.get('comp'))}"]:not([visibility=hidden])`;

        // A shared component link is an explicit request to go to that component, so it
        // zooms to fit even when the zoom lock is on.
        await this.dollyZoom($svg, selector, {ignoreZoomLock: true});

        event('visit_component_link', {
          src: this.headPreviewUrl,
        });
      }
    },

    /** Frame the elements matching `selector` (see dollyZoomToSelector). */
    async dollyZoom(svgEl: SVGSVGElement, selector: string, {ignoreZoomLock = false} = {}) {
      if (!this.panZoom) return;
      // Only use zoom lock for diffs, plain file view zooms to fit
      if (!this.isDiff) {
        await dollyZoomToSelector(this.panZoom, svgEl, selector);
        return;
      }
      const zoomLock = useZoomLockStore();

      // If locked, hold the last zoom level reached on any sheet. If none has been
      // recorded yet, hold this sheet's current level.
      let lockedScale: number | undefined;
      if (zoomLock.isLocked && !ignoreZoomLock) {
        lockedScale = zoomLock.scale ?? this.panZoom.getScale();
      }

      await dollyZoomToSelector(this.panZoom, svgEl, selector, {lockedScale});

      // Record the level this navigation ended at, locked or not, so turning the
      // lock on after a fit-zoom holds that level on the next sheet.
      zoomLock.recordScale(this.panZoom.getScale());
    },

    onDiffError(fetchRetryer: FetchRetryer) {
      this.isDiffLoadError = true;
      if (fetchRetryer.response) {
        this.displayDiffResponseError(fetchRetryer.response);
      } else {
        this.displayUnknownError(fetchRetryer.errorDetails);
      }
    },

    /**
     * @private
     * @return {Element | null}
     */
    findASLoading() {
      const container = this.$el.closest('.diff-file-box') ?? this.$el.closest('.non-diff-file-content');
      if (!container) return null;
      return container.querySelector('.allspice-loading');
    },

    flip(flipped: boolean | null = null) {
      if (flipped === null) {
        flipped = !this.isFlipped;
      }
      this.isFlipped = flipped;

      const $svg: SVGSVGElement = (this.$refs.inlineSvg as ComponentPublicInstance).$el;
      // we need to push the actual flipping to the next tick so that the loading indicator displays
      this.setLoadingIndicator(true);
      window.setTimeout(() => {
        this.flipBoard($svg, flipped);

        const regionParent = selectRegionParent($svg);
        if (regionParent) {
          regionParent.classList.toggle('flipped', flipped);
        }

        this.setLoadingIndicator(false);
      }, 0);
    },

    /**
     * Modify the SVG so that the PCB appears flipped over
     *
     * @param svg SVGSVGElement the SVG element to modify
     * @returns SVGSVGElement the modified SVG element
     */
    flipBoard(svg: SVGSVGElement, flipped = true) {
      const layerGroupsToReverse = [
        'g[data-group=foreground]',
        '.diff > g',
        '#selected-net',
      ];
      for (const group of svg.querySelectorAll(layerGroupsToReverse.join(', '))) {
        group.replaceChildren(...Array.from(group.childNodes).reverse());
      }

      svg.classList.toggle('flipped', flipped);
      return svg;
    },
  },
});

</script>

<template>
  <div
    ref="wrapper"
    class="svg-wrapper"
  >
    <ecad-inspector-frame
      ref="ecadInspectorFrame"
      :is-diff="isDiff"
      @inspector-did-close="inspectorNetClose"
    />
    <div class="svg-schematic">
      <div
        :class="svgContainerClassNames"
        class="selectable-region-parent"
      >
        <div
          ref="selectableRegion"
          class="selectable-region"
        >
          <div
            ref="panZoomContainer"
            class="pan-zoom-container"
          >
            <div
              ref="panZoom"
              class="pan-zoom"
            >
              <div
                :class="svgClassNames"
                :data-as-view-coords="dataAsViewCoords"
                :data-as-aspect-ratio="dataAsAspectRatio"
              >
                <generated-inline-svg
                  ref="inlineSvg"
                  :src="headPreviewUrl"
                  :transform-container="transformContainer"
                  :transform-source="transformHeadSvg"
                  :transform-children="transformChildren"
                  @load="onHeadSvgLoad()"
                  @error="onHeadSvgError($event)"
                />
                <allspice-snippet-reference
                  v-if="!isLoading && referenceElement"
                  :key="(referenceElement as any)"
                  :reference-element="referenceElement"
                  :pan-zoom="panZoom"
                />
              </div>
            </div>
          </div>
        </div>
        <div
          v-if="isLoading"
          class="loading-container"
        >
          <div class="is-loading" />
          LOADING design file
        </div>
      </div>
      <div v-if="!isLoading && isLoadError && displayDefaultOnError">
        <img
          class="ui active centered inline text"
          :src="failedImageUrl"
        >
      </div>
    </div>
    <div
      v-if="isPanZoomEnabled"
      class="pan-zoom-help-text"
      :class="panZoomHelpClassNames"
    >
      {{ panZoomHelpText }}
    </div>
    <div
      v-if="showErrorDetails"
      class="ui negative message"
    >
      {{ errorDetailsText }}
    </div>
  </div>
</template>

<style scoped>

.selectable-region-parent {
  justify-content: center;
  align-items: center;
  display: flex;
}

.loading-container {
  z-index: 6;
  position: absolute;
}

.loading-container .is-loading {
  height: 80px;
}

.svg-wrapper {
  margin: 4px;
  position: relative;
}

.svg-schematic {
  box-sizing: border-box;
  border: solid 1px #495057;
  width: 100%;
  max-height: 100%;
}

/* SVG viewport fills .selectable-region (height set by controller); zoom via viewBox, no CSS transform/letterbox. */
.svg-schematic .selectable-region {
  position: relative;
  /* Inherited by the svg: stop pan-drag from selecting text and suppress WebKit's I-beam over SVG <text>. */
  cursor: default;
  user-select: none;
}

.svg-schematic .pan-zoom-container,
.svg-schematic .pan-zoom {
  position: absolute;
  inset: 0;
}

/* :deep so Vue scopes this onto dynamically generated child SVGs. */
.svg-schematic :deep(svg.schematic),
.svg-schematic :deep(svg.schematic_svg) {
  all: initial;
  /* Cursor is set per pointer/selection mode, so don't reset it. */
  cursor: inherit;
  pointer-events: inherit;

  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  display: block;
  shape-rendering: optimizeSpeed;
  text-rendering: optimizeSpeed;
  buffered-rendering: dynamic;
}

.svg-schematic :deep(svg.flipped) {
  transform: scale(-1, 1);
}

.pan-zoom-help-text {
  opacity: 0.7;
  text-align: center;
  min-height: 1lh;
}

.pan-zoom-help-text.hidden {
  visibility: hidden;
}

</style>
